import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, MessageCircle, PhoneCall, Plug, Workflow } from 'lucide-react';
import { SlideStage } from '../../components/deck/SlideStage';
import { acquisition, businessCapabilities, businessChannels, exposureLoop, exposureStatement, gtmStages } from '../../data/deckSlide10';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
const CAPABILITY_ICONS = {
  voice: PhoneCall,
  whatsapp: MessageCircle,
  website: Globe,
  workflows: Workflow,
  integrations: Plug
} as const;
const COLUMN_X = [112, 592, 1048];
const COLUMN_W = [352, 360, 440];
const HEAD_Y = 336;
type Stage = 0 | 1 | 2 | 3 | 4;
export function GoToMarketSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(800, 1); // acquisition
    at(3000, 2); // product-led exposure loop
    at(5200, 3); // business expansion
    at(7400, 4); // b2b channels + takeaway
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2304" data-mp="2a6plkf4z3drb">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2305" data-mp="2a89286ujjqtm">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 6% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2306" data-mp="2xws9qk5rnzkn" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2307" data-mp="2swnu0wcon8vh">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2308" data-mp="3k5h5lh6rthjl" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2309" data-mp="3k7jutz03n6n9">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2310" data-mp="3afe1oly42j6h">
          11 · Go-to-Market
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 126,
        width: 700
      }} data-id="element-2311" data-mp="2whzbhf01a7dg">
          {['Acquire the individual.', 'Expand into the business.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.15,
          ease
        }} data-id="element-2312" data-mp="pondc8w1iiy6" data-mpi={i}>
                {line}
              </motion.span>)}
        </h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 236,
        width: 660
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2313" data-mp="11zego685jhyp">
          A measurable U.S.-first growth engine — from creator-led acquisition
          to product-led exposure and higher-value business expansion.
        </motion.p>

        {/* Stage headers */}
        {gtmStages.map((s, i) => <motion.div key={s.index} className="absolute border-t border-[#0B1712]/[0.09] pt-4" style={{
        left: COLUMN_X[i],
        top: HEAD_Y,
        width: COLUMN_W[i]
      }} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: shown(i + 1 as Stage) ? 1 : 0.25,
        y: 0
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2314" data-mp="o9ug9721mwsk" data-mpi={i}>
            <span className="text-[11px] font-semibold tracking-[0.24em] text-vexu-700/70 tabular-nums" data-id="element-2315" data-mp="33n4qqaem2o6f">
              {s.index}
            </span>
            <p className="mt-1.5 text-[17px] font-semibold tracking-[-0.01em] text-[#0B1712]" data-id="element-2316" data-mp="3clgmsry8ye69">
              {s.title}
            </p>
          </motion.div>)}

        {/* Stage connectors */}
        {[{
        x: 494,
        at: 2 as Stage
      }, {
        x: 950,
        at: 3 as Stage
      }].map(c => <motion.div key={c.x} className="absolute h-px" style={{
        left: c.x,
        top: 420,
        backgroundColor: 'rgba(0,200,83,0.35)'
      }} initial={{
        width: 0
      }} animate={{
        width: shown(c.at) ? 62 : 0
      }} transition={{
        duration: 0.7,
        ease
      }} data-id="element-2317" data-mp="453msryma692" />)}

        {/* 01 — Acquire */}
        <div className="absolute" style={{
        left: COLUMN_X[0],
        top: 418,
        width: COLUMN_W[0]
      }} data-id="element-2318" data-mp="sscrg7jie82y">
          <motion.p className="text-[22px] font-semibold leading-tight tracking-[-0.015em] text-[#0B1712]" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: shown(1) ? 1 : 0,
          y: shown(1) ? 0 : 8
        }} transition={{
          duration: 0.8,
          ease
        }} data-id="element-2319" data-mp="xpcusaoz4wqd">
            {acquisition.primary}
          </motion.p>

          <motion.div className="mt-4 flex items-center gap-2.5 text-[13px] text-[#33463B]" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(1) ? 1 : 0
        }} transition={{
          duration: 0.7,
          delay: 0.3,
          ease
        }} data-id="element-2320" data-mp="erif5dtcbmz0">
            {acquisition.flow.map((node, i) => <React.Fragment key={node} data-id="element-2321" data-mp="3n8d8a1kc5szw" data-mpi={i}>
                {i > 0 && <span className="text-[#C3CCC5]" data-id="element-2322" data-mp="3r8no05xxjxry">→</span>}
                <span className={i === 1 ? 'font-semibold' : undefined} data-id="element-2323" data-mp="3r85matsi0rcn">
                  {node}
                </span>
              </React.Fragment>)}
          </motion.div>

          <motion.div className="mt-6" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(1) ? 1 : 0
        }} transition={{
          duration: 0.7,
          delay: 0.6,
          ease
        }} data-id="element-2324" data-mp="1syg6vso353ly">
            <span className="inline-flex items-center gap-2 rounded-full border border-vexu/25 bg-vexu/[0.07] px-3 py-1.5 text-[12px] font-semibold text-vexu-700" data-id="element-2325" data-mp="1j2muc9ad9a46">
              {acquisition.proof}
            </span>
            <p className="mt-3 text-[11.5px] leading-snug text-[#A3AFA7]" data-id="element-2326" data-mp="2bnmqrj0m819c">
              {acquisition.detail}
            </p>
          </motion.div>

          <motion.p className="mt-9 text-[12.5px] text-[#8A998F]" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(1) ? 1 : 0
        }} transition={{
          duration: 0.7,
          delay: 0.9,
          ease
        }} data-id="element-2327" data-mp="22psxyi227pqx">
            {acquisition.secondary.join(' · ')}
          </motion.p>
        </div>

        {/* 02 — Product-led exposure loop */}
        <div className="absolute" style={{
        left: COLUMN_X[1],
        top: 418,
        width: COLUMN_W[1]
      }} data-id="element-2328" data-mp="36yt46h6bxnoy">
          {/* return arc, closing the loop */}
          <svg className="absolute" style={{
          left: -40,
          top: 6,
          width: 46,
          height: 180
        }} viewBox="0 0 46 180" fill="none" aria-hidden="true" data-id="element-2329" data-mp="3bvt8onnj6idy">
            <defs data-id="element-2330" data-mp="3lq1wwz5j3p6b">
              <marker id="gtm-loop-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" data-id="element-2331" data-mp="3qmk1nz0uznin">
                <path d="M0 0 L6 3 L0 6 z" fill="rgba(0,153,64,0.5)" data-id="element-2332" data-mp="3icgd3pjwdie" />
              </marker>
            </defs>
            <motion.path d="M40 172 C4 172, 4 8, 34 8" stroke="rgba(0,200,83,0.4)" strokeWidth="1" markerEnd="url(#gtm-loop-arrow)" initial={{
            pathLength: 0
          }} animate={{
            pathLength: shown(2) ? 1 : 0
          }} transition={{
            duration: 1.1,
            delay: 0.75,
            ease
          }} data-id="element-2333" data-mp="20bo4jehkey65" />
          </svg>

          {exposureLoop.map((node, i) => {
          const anchor = i === 0 || i === exposureLoop.length - 1;
          return <motion.div key={node} className="relative flex items-center gap-3" style={{
            height: 56
          }} initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: shown(2) ? 1 : 0,
            x: shown(2) ? 0 : -6
          }} transition={{
            duration: 0.6,
            delay: i * 0.22,
            ease
          }} data-id="element-2334" data-mp="fsvdtt3yj7h8" data-mpi={i}>
                {i > 0 && <span className="absolute w-px" style={{
              left: 2.5,
              top: -22,
              height: 22,
              backgroundColor: 'rgba(0,153,64,0.22)'
            }} data-id="element-2335" data-mp="3t5nfw23vzhkv" />}
                <span className="rounded-full" style={{
              width: 6,
              height: 6,
              backgroundColor: anchor ? '#009940' : 'rgba(0,153,64,0.3)'
            }} data-id="element-2336" data-mp="2qgqnhl9dc7sk" />
                <span className={`text-[15px] ${anchor ? 'font-semibold text-[#0B1712]' : 'text-[#33463B]'}`} data-id="element-2337" data-mp="2mdu25msg1xkx">
                  {node}
                </span>
              </motion.div>;
        })}

          <motion.p className="mt-6 text-[13.5px] leading-relaxed text-vexu-700" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(2) ? 1 : 0
        }} transition={{
          duration: 0.8,
          delay: 1.1,
          ease
        }} data-id="element-2338" data-mp="30ctm4h50hoar">
            {exposureStatement}
          </motion.p>
        </div>

        {/* 03 — Business expansion */}
        <div className="absolute" style={{
        left: COLUMN_X[2],
        top: 418,
        width: COLUMN_W[2]
      }} data-id="element-2339" data-mp="1od1r63ihp5j">
          <motion.div className="flex items-center gap-3 text-[13.5px] text-[#5A6B60]" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(3) ? 1 : 0
        }} transition={{
          duration: 0.7,
          ease
        }} data-id="element-2340" data-mp="f5062s68hbtz">
            <span className="font-medium text-[#33463B]" data-id="element-2341" data-mp="a6ovugc10zb8">VEXU App</span>
            <span className="h-px w-6 bg-[#0B1712]/15" data-id="element-2342" data-mp="3slzckx2ubwgv" />
            <span className="italic" data-id="element-2343" data-mp="3skeljhfqivnr">Need more for your business?</span>
          </motion.div>

          <motion.div className="mt-3 flex items-center gap-3" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: shown(3) ? 1 : 0,
          y: shown(3) ? 0 : 8
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease
        }} data-id="element-2344" data-mp="18cubj9mgh40">
            <span className="h-px w-6" style={{
            background: '#009940'
          }} data-id="element-2345" data-mp="39mlk6jkretsh" />
            <p className="text-[22px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2346" data-mp="3elom3v5g0jw8">
              VEXU Business
            </p>
          </motion.div>

          <div className="mt-6 flex gap-7" data-id="element-2347" data-mp="2v0ep41nbq153">
            {businessCapabilities.map((cap, i) => {
            const Icon = CAPABILITY_ICONS[cap.id];
            return <motion.div key={cap.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: shown(3) ? 1 : 0,
              y: shown(3) ? 0 : 6
            }} transition={{
              duration: 0.6,
              delay: 0.4 + i * 0.12,
              ease
            }} data-id="element-2348" data-mp="341w4tiwtk0t9" data-mpi={i}>
                  <Icon className="h-[15px] w-[15px] text-vexu-700" data-id="element-2349" data-mp="390ph580ddlru" />
                  <p className="mt-2 text-[11.5px] font-medium leading-tight text-[#33463B]" data-id="element-2350" data-mp="3nou08d3c3zb5">
                    {cap.label}
                  </p>
                  {cap.note && <p className="mt-1 text-[10.5px] leading-tight text-[#A3AFA7]" data-id="element-2351" data-mp="3irts7wmxcmhe">
                      {cap.note}
                    </p>}
                </motion.div>;
          })}
          </div>

          <motion.p className="mt-12 text-[12.5px] text-[#8A998F]" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(4) ? 1 : 0
        }} transition={{
          duration: 0.8,
          ease
        }} data-id="element-2352" data-mp="2pdj2ypl6bhp9">
            {businessChannels.join(' · ')}
          </motion.p>
        </div>

        {/* Takeaway */}
        <motion.p className="absolute text-[15px] font-medium text-[#33463B]" style={{
        left: 112,
        bottom: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.3,
        ease
      }} data-id="element-2353" data-mp="3357byk04lq20">
          Acquire once. Expand across users, channels and businesses.
        </motion.p>
      </div>
    </SlideStage>;
}